import { useState, useRef, useEffect } from 'react'
import { motion, useInView } from 'framer-motion'
import ScrollAnimation from '../animations/ScrollAnimation'
import { getSocialLinks, getContent } from '../../services/api'
import type { SocialLink, Content } from '../../types/database'

const transition = { duration: 0.6, ease: [0.25, 1, 0.5, 1] }
const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([])
  const [content, setContent] = useState<Content | null>(null)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  useEffect(() => {
    const loadData = async () => {
      const [links, contentData] = await Promise.all([
        getSocialLinks(),
        getContent(),
      ])
      setSocialLinks(links)
      setContent(contentData)
    }
    loadData()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')

    await new Promise((resolve) => setTimeout(resolve, 1500))
    console.log('Form submitted:', formData)
    
    setSubmitStatus('success')
    setIsSubmitting(false)
    setFormData({ name: '', email: '', message: '' })
    setTimeout(() => setSubmitStatus('idle'), 3000)
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-background"
    >
      <div className="container mx-auto max-w-7xl relative z-10">
        <ScrollAnimation variant="slideUp" delay={0.2} className="text-center mb-16">
          <motion.span
            className="inline-block px-4 py-2 text-xs font-medium text-accent mb-6 uppercase tracking-widest"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={springConfig}
          >
            Get In Touch
          </motion.span>
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold mb-6 font-display leading-[1.1] tracking-tight">
            <span className="text-gradient-glow">Let's Work</span>{' '}
            <span className="text-text-primary">Together</span>
          </h2>
          <p className="text-base sm:text-lg text-text-primary/70 max-w-xl mx-auto leading-relaxed mb-16">
            Have a project in mind? I'd love to hear about it. Send me a message
            and let's discuss how we can bring your ideas to life.
          </p>
        </ScrollAnimation>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ ...transition, delay: 0.3 }}
            onSubmit={handleSubmit}
            className="neu p-10 space-y-6"
          >
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium mb-3 text-text-primary/70 uppercase tracking-wide"
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-5 py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal"
                placeholder="Your Name"
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium mb-3 text-text-primary/70 uppercase tracking-wide"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-5 py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal"
                placeholder="your.email@example.com"
              />
            </div>
            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium mb-3 text-text-primary/70 uppercase tracking-wide"
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="w-full px-5 py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all resize-none font-normal"
                placeholder="Tell me about your project..."
              />
            </div>
            <motion.button
              type="submit"
              data-magnetic
              disabled={isSubmitting}
              className="w-full px-10 py-4 neu rounded-2xl font-medium text-text-primary text-base neu-hover"
              whileHover={{ scale: isSubmitting ? 1 : 1.03, y: -2 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              transition={springConfig}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </motion.button>
            {submitStatus === 'success' && (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-accent text-center text-sm"
              >
                Message sent successfully! I'll get back to you soon.
              </motion.p>
            )}
          </motion.form>

          {/* Social Links & Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ ...transition, delay: 0.4 }}
            className="space-y-8"
          >
            <div className="neu p-10">
              <h3 className="text-2xl font-semibold mb-6 text-text-primary">
                Connect With Me
              </h3>
              <p className="text-text-primary/70 mb-8 leading-relaxed">
                Feel free to reach out through any of these channels. I'm always
                open to discussing new projects, creative ideas, or opportunities
                to be part of your vision.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {socialLinks.length > 0 ? (
                  socialLinks.map((link) => (
                    <motion.a
                      key={link.id}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-magnetic
                      className="neu p-4 flex items-center gap-4 group rounded-2xl neu-hover"
                      whileHover={{ scale: 1.03, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      transition={springConfig}
                    >
                      <span className="text-2xl">{link.icon}</span>
                      <span className="text-text-primary/70 group-hover:text-text-primary font-medium">
                        {link.name}
                      </span>
                    </motion.a>
                  ))
                ) : (
                  <p className="text-text-primary/50 text-sm col-span-2">No social links configured yet.</p>
                )}
              </div>
            </div>

            {content && (
              <div className="neu p-10">
                <h3 className="text-2xl font-semibold mb-4 text-text-primary">
                  Quick Info
                </h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-text-primary/70 text-sm">Location</span>
                    <p className="text-text-primary">{content.quick_info_location || 'Available Worldwide'}</p>
                  </div>
                  <div>
                    <span className="text-text-primary/70 text-sm">Response Time</span>
                    <p className="text-text-primary">{content.quick_info_response_time || 'Usually within 24 hours'}</p>
                  </div>
                  <div>
                    <span className="text-text-primary/70 text-sm">Availability</span>
                    <p className="text-text-primary">{content.quick_info_availability || 'Open to new projects'}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  )
}
